import { useEffect } from 'react';

import { DbtDagNode, ZoomMode } from '../dag/DagInterfaces';
import { DagGraph } from '../dag/internalDag';
import { useViewportContext } from '../providers/ViewportProvider';

type FitViewOptionParams<TDbtDagNode extends DbtDagNode> = {
  zoomMode: ZoomMode | undefined;
  primaryNodeIds: string[] | undefined;
  dagGraph: DagGraph<TDbtDagNode> | undefined;
};

const getNodesToFit = <TDbtDagNode extends DbtDagNode>(
  zoomMode: ZoomMode,
  primaryNodeIds: string[],
  dagGraph: DagGraph<TDbtDagNode> | undefined,
) => {
  if (zoomMode === 'fitAll' || primaryNodeIds.length === 0) return [];
  if (zoomMode === 'primary') {
    return primaryNodeIds.map((id) => ({ id }));
  }
  const primaryNodes = primaryNodeIds.map((id) => (dagGraph ?? {})[id]).filter(Boolean);
  if (zoomMode === '1+primary+1' && dagGraph && primaryNodes.length > 0) {
    const childNodes = primaryNodes.flatMap(({ children }) => {
      return children.map((id) => dagGraph[id]);
    });
    const parentNodes = primaryNodes.flatMap(({ parents }) => {
      return parents.map((id) => dagGraph[id]);
    });
    return [...primaryNodes, ...childNodes, ...parentNodes];
  }
  return [];
};

export const useSyncNodesToFit = <TDbtDagNode extends DbtDagNode>({
  zoomMode = 'fitAll',
  primaryNodeIds = [],
  dagGraph,
}: FitViewOptionParams<TDbtDagNode>): void => {
  const viewportContext = useViewportContext();
  useEffect(() => {
    const nodesToFit = getNodesToFit(zoomMode, primaryNodeIds, dagGraph);
    viewportContext.setNodesToFit(nodesToFit);
  }, [dagGraph, primaryNodeIds, viewportContext, zoomMode]);
};
